import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils/cn';
import type {
  AclModuleWithChildren,
  PermissionFlags,
  PermissionsMap,
} from '../helpers';

const LEAF_KEYS = ['read', 'add', 'edit', 'delete', 'view_permission'] as const;
type LeafKey = (typeof LEAF_KEYS)[number];

interface AclPermissionMatrixProps {
  modules: AclModuleWithChildren[];
  permissions: PermissionsMap;
  expanded: Record<string, boolean>;
  onToggle: (id: string, key: LeafKey, next: boolean) => void;
  onToggleAll: (id: string, keys: readonly LeafKey[], next: boolean) => void;
  onExpand: (id: string, next: boolean) => void;
  isLoading?: boolean;
}

/**
 * Hierarchical permission table — parent rows show only `read` and
 * `view_permission` columns when they have children (because the child
 * rows carry the granular CRUD bits). Leaf modules and children show
 * all five columns: read / add / edit / delete / view_permission.
 *
 * The "all" cell on each row is a tri-state convenience toggle: it flips
 * every applicable column on or off in one click.
 */
export function AclPermissionMatrix({
  modules,
  permissions,
  expanded,
  onToggle,
  onToggleAll,
  onExpand,
  isLoading,
}: AclPermissionMatrixProps) {
  if (isLoading) {
    return (
      <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
        Loading modules…
      </div>
    );
  }
  if (modules.length === 0) {
    return (
      <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
        No ACL modules exist yet. Create some on the Modules page first.
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Module</th>
            <th className="w-20 px-3 py-2 text-center">Read</th>
            <th className="w-20 px-3 py-2 text-center">Add</th>
            <th className="w-20 px-3 py-2 text-center">Edit</th>
            <th className="w-20 px-3 py-2 text-center">Delete</th>
            <th className="w-24 px-3 py-2 text-center">View</th>
            <th className="w-20 px-3 py-2 text-center">All</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => {
            const hasChildren = m.children.length > 0;
            const isExpanded = Boolean(expanded[m.mname]);
            const parentPerms = permissions[m.mname] ?? {};

            return (
              <ParentAndChildRows
                key={m.mid}
                module={m}
                hasChildren={hasChildren}
                isExpanded={isExpanded}
                parentPerms={parentPerms}
                permissions={permissions}
                onToggle={onToggle}
                onToggleAll={onToggleAll}
                onExpand={onExpand}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ParentAndChildRows({
  module: m,
  hasChildren,
  isExpanded,
  parentPerms,
  permissions,
  onToggle,
  onToggleAll,
  onExpand,
}: {
  module: AclModuleWithChildren;
  hasChildren: boolean;
  isExpanded: boolean;
  parentPerms: PermissionFlags;
  permissions: PermissionsMap;
  onToggle: AclPermissionMatrixProps['onToggle'];
  onToggleAll: AclPermissionMatrixProps['onToggleAll'];
  onExpand: AclPermissionMatrixProps['onExpand'];
}) {
  /*
   * Parent rows with children are PURELY STRUCTURAL — no permission
   * checkboxes. All granting happens at the leaf level. The right-side
   * "All" cell cascades to every child row (toggling all 5 flags on
   * each child). Parent-level `read` / `view_permission` get computed
   * server-side from the children (see buildModuleOperation).
   *
   * Leaf-only rows (no children) still show all five permission
   * checkboxes + their own All toggle.
   */
  const isStructuralParent = hasChildren;
  const leafAllChecked = !isStructuralParent
    && LEAF_KEYS.every((k) => parentPerms[k] === true);
  const childrenAllChecked =
    isStructuralParent &&
    m.children.length > 0 &&
    m.children.every((c) =>
      LEAF_KEYS.every((k) => (permissions[c.moduleName] ?? {})[k] === true),
    );

  const cascadeChildren = (next: boolean) => {
    for (const c of m.children) {
      onToggleAll(c.moduleName, LEAF_KEYS, next);
    }
  };

  return (
    <>
      <tr className="border-b last:border-b-0">
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onExpand(m.mname, !isExpanded)}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="inline-block h-6 w-6" />
            )}
            <span className="font-medium">{m.mname}</span>
            {hasChildren && (
              <span className="text-xs text-muted-foreground">
                ({m.children.length} child{m.children.length === 1 ? '' : 'ren'})
              </span>
            )}
          </div>
        </td>

        {isStructuralParent ? (
          // Structural parent — no permission checkboxes at all. The
          // dashes keep column alignment with the leaf rows below.
          <>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center text-muted-foreground/30">—</td>
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={childrenAllChecked}
                onCheckedChange={(v) => cascadeChildren(Boolean(v))}
                aria-label={`Grant every permission on all ${m.children.length} children of ${m.mname}`}
              />
            </td>
          </>
        ) : (
          <>
            <PermissionCell
              keyName="read"
              checked={parentPerms.read}
              onChange={(next) => onToggle(m.mname, 'read', next)}
            />
            <PermissionCell
              keyName="add"
              checked={parentPerms.add}
              onChange={(next) => onToggle(m.mname, 'add', next)}
            />
            <PermissionCell
              keyName="edit"
              checked={parentPerms.edit}
              onChange={(next) => onToggle(m.mname, 'edit', next)}
            />
            <PermissionCell
              keyName="delete"
              checked={parentPerms.delete}
              onChange={(next) => onToggle(m.mname, 'delete', next)}
            />
            <PermissionCell
              keyName="view_permission"
              checked={parentPerms.view_permission}
              onChange={(next) => onToggle(m.mname, 'view_permission', next)}
            />
            <td className="px-3 py-2 text-center">
              <Checkbox
                checked={leafAllChecked}
                onCheckedChange={(v) =>
                  onToggleAll(m.mname, LEAF_KEYS, Boolean(v))
                }
                aria-label={`Select all ${m.mname} permissions`}
              />
            </td>
          </>
        )}
      </tr>

      {hasChildren &&
        isExpanded &&
        m.children.map((c) => {
          const cp = permissions[c.moduleName] ?? {};
          const cAll = LEAF_KEYS.every((k) => cp[k] === true);
          return (
            <tr
              key={`${m.mid}-${c.moduleName}`}
              className={cn('border-b bg-muted/20 last:border-b-0')}
            >
              <td className="px-3 py-2 pl-12 text-sm text-foreground/80">
                ↳ {c.moduleName}
              </td>
              <PermissionCell
                keyName="read"
                checked={cp.read}
                onChange={(next) => onToggle(c.moduleName, 'read', next)}
              />
              <PermissionCell
                keyName="add"
                checked={cp.add}
                onChange={(next) => onToggle(c.moduleName, 'add', next)}
              />
              <PermissionCell
                keyName="edit"
                checked={cp.edit}
                onChange={(next) => onToggle(c.moduleName, 'edit', next)}
              />
              <PermissionCell
                keyName="delete"
                checked={cp.delete}
                onChange={(next) => onToggle(c.moduleName, 'delete', next)}
              />
              <PermissionCell
                keyName="view_permission"
                checked={cp.view_permission}
                onChange={(next) =>
                  onToggle(c.moduleName, 'view_permission', next)
                }
              />
              <td className="px-3 py-2 text-center">
                <Checkbox
                  checked={cAll}
                  onCheckedChange={(v) =>
                    onToggleAll(c.moduleName, LEAF_KEYS, Boolean(v))
                  }
                  aria-label={`Select all ${c.moduleName} permissions`}
                />
              </td>
            </tr>
          );
        })}
    </>
  );
}

function PermissionCell({
  keyName,
  checked,
  onChange,
}: {
  keyName: LeafKey;
  checked: boolean | undefined;
  onChange: (next: boolean) => void;
}) {
  return (
    <td className="px-3 py-2 text-center">
      <Checkbox
        checked={Boolean(checked)}
        onCheckedChange={(v) => onChange(Boolean(v))}
        aria-label={keyName}
      />
    </td>
  );
}
